import React, { createContext, useContext, useEffect, useRef, useState, useCallback, useMemo } from 'react';
import type { UserProfile, AuthSession } from '../types/forms';
import {
  signIn as authSignIn,
  signUp as authSignUp,
  signOut as authSignOut,
  sendPasswordReset as authSendPasswordReset,
  updatePassword as authUpdatePassword,
  updateEmail as authUpdateEmail,
  onAuthStateChange,
  updateProfile as authUpdateProfile,
} from '../lib/authInstrumented';
import { debugLog } from '../lib/debugLog';

// =====================================================
// CONTEXTO DE AUTENTICAÇÃO - ONEFORM
// =====================================================

interface AuthContextType extends AuthSession {
  signIn: (email: string, password: string) => Promise<{ error: string | null }>;
  signUp: (email: string, password: string, fullName?: string) => Promise<{ error: string | null }>;
  signOut: () => Promise<void>;
  sendPasswordReset: (email: string) => Promise<{ error: string | null }>;
  updatePassword: (newPassword: string) => Promise<{ error: string | null }>;
  updateEmail: (newEmail: string) => Promise<{ error: string | null }>;
  updateProfile: (updates: Partial<Pick<UserProfile, 'full_name' | 'avatar_url' | 'username'>>) => Promise<{ error: string | null }>;
  isAdmin: boolean;
  isSuperAdmin: boolean;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<UserProfile | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const userRef = useRef<UserProfile | null>(null);

  // Manter ref sincronizado com state (para callbacks estáveis)
  useEffect(() => {
    userRef.current = user;
  }, [user]);

  // Listener único de auth — INITIAL_SESSION faz a carga inicial, sem race condition
  useEffect(() => {
    debugLog('info', 'AuthProvider', 'Registrando listener de auth');

    const { data: { subscription } } = onAuthStateChange((newUser) => {
      setUser(prev => {
        if (!newUser && !prev) {
          debugLog('info', 'AuthProvider.setUser', 'Sem mudança (ambos null)');
          return prev;
        }
        if (!newUser || !prev) {
          debugLog('info', 'AuthProvider.setUser', `Mudou: ${prev?.email || 'null'} → ${newUser?.email || 'null'}`);
          return newUser;
        }
        if (prev.id === newUser.id && prev.email === newUser.email && prev.role === newUser.role && prev.full_name === newUser.full_name && prev.avatar_url === newUser.avatar_url && prev.username === newUser.username && prev.is_superadmin === newUser.is_superadmin) {
          debugLog('info', 'AuthProvider.setUser', 'Mesmo perfil, sem re-render');
          return prev;
        }
        debugLog('info', 'AuthProvider.setUser', 'Perfil atualizado', { email: newUser.email });
        return newUser;
      });
      setLoading(false);
    });

    // Safety net: se INITIAL_SESSION não disparar em 10s, desbloquear UI
    const timeout = setTimeout(() => {
      setLoading(prev => {
        if (prev) {
          debugLog('warn', 'AuthProvider', 'Timeout: auth check não completou em 10s');
        }
        return false;
      });
    }, 10_000);

    return () => {
      subscription.unsubscribe();
      clearTimeout(timeout);
    };
  }, []);

  const signIn = useCallback(async (email: string, password: string) => {
    setError(null);

    const result = await authSignIn(email, password);

    if (result.error) {
      setError(result.error);
      return { error: result.error };
    }

    if (!result.user) {
      const errMsg = 'Erro ao carregar perfil do usuário';
      setError(errMsg);
      return { error: errMsg };
    }

    setUser(result.user);
    return { error: null };
  }, []);

  const signUp = useCallback(async (email: string, password: string, fullName?: string) => {
    setError(null);

    const result = await authSignUp(email, password, fullName);

    if (result.error) {
      setError(result.error);
      return { error: result.error };
    }

    return { error: null };
  }, []);

  const signOut = useCallback(async () => {
    await authSignOut();
    setUser(null);
  }, []);

  const sendPasswordReset = useCallback(async (email: string) => {
    setError(null);
    const result = await authSendPasswordReset(email);

    if (result.error) {
      setError(result.error);
      return { error: result.error };
    }

    return { error: null };
  }, []);

  const updatePassword = useCallback(async (newPassword: string) => {
    setError(null);
    const result = await authUpdatePassword(newPassword);

    if (result.error) {
      setError(result.error);
      return { error: result.error };
    }

    return { error: null };
  }, []);

  const updateEmail = useCallback(async (newEmail: string) => {
    setError(null);
    const result = await authUpdateEmail(newEmail);

    if (result.error) {
      setError(result.error);
      return { error: result.error };
    }

    // Functional update evita dependência no objeto user
    setUser(prev => prev ? { ...prev, email: newEmail } : prev);

    return { error: null };
  }, []);

  const updateProfile = useCallback(async (updates: Partial<Pick<UserProfile, 'full_name' | 'avatar_url' | 'username'>>) => {
    const currentUser = userRef.current;
    if (!currentUser) {
      return { error: 'Usuário não autenticado' };
    }

    setError(null);
    const result = await authUpdateProfile(currentUser.id, updates);

    if (result.error) {
      setError(result.error);
      return { error: result.error };
    }

    if (result.profile) {
      setUser(result.profile);
    }

    return { error: null };
  }, []);

  const isAdmin = user?.role === 'admin';
  const isSuperAdmin = user?.is_superadmin === true;

  const value = useMemo<AuthContextType>(() => ({
    user,
    loading,
    error,
    signIn,
    signUp,
    signOut,
    sendPasswordReset,
    updatePassword,
    updateEmail,
    updateProfile,
    isAdmin,
    isSuperAdmin,
  }), [user, loading, error, signIn, signUp, signOut, sendPasswordReset, updatePassword, updateEmail, updateProfile, isAdmin, isSuperAdmin]);

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);

  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }

  return context;
};

// Hook para verificar se usuário é admin
export const useIsAdmin = () => {
  const { user } = useAuth();
  return user?.role === 'admin';
};

// Hook para verificar se usuário é superadmin (plataforma)
export const useIsSuperAdmin = () => {
  const { user } = useAuth();
  return user?.is_superadmin === true;
};

// Hook para verificar se está autenticado
export const useIsAuthenticated = () => {
  const { user, loading } = useAuth();
  return { isAuthenticated: !!user, loading };
};
